/*eslint-disable*/
import React from 'react';
import {
	useTable,
	useRowSelect,
	useAsyncDebounce,
	useGlobalFilter,
	useSortBy,
	usePagination
} from 'react-table';
import classnames from 'classnames';

// @material-ui/core components
import { makeStyles } from '@material-ui/core/styles';
import FormControl from '@material-ui/core/FormControl';
import FormLabel from '@material-ui/core/FormLabel';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Checkbox from '@material-ui/core/Checkbox';
import SearchIcon from '@material-ui/icons/Search';

import MoreVert from '@material-ui/icons/MoreVert';
import Delete from "@material-ui/icons/Delete";

// core components
import CustomInput from 'components/CustomInput/CustomInput';
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import Pagination from 'components/Pagination/Pagination';
import Button from 'components/CustomButtons/Button';
import ButtonMenuList from 'components/CustomButtons/ButtonMenuList';
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator';
import DateRangeFilter  from 'components/DateRangeFilter/DateRangeFilter';
import { scrollToTop } from 'utils/utils';
import { useHistory } from 'react-router-dom';

import { verifyLength } from 'plugins/validation/validation-rules';
import reactTableStyle from 'assets/jss/material-dashboard-pro-react/components/reactTableStyle';

const useStyles = makeStyles(reactTableStyle);

const IndeterminateCheckbox = React.forwardRef(
	({ indeterminate, ...rest }, ref) => {
		const defaultRef = React.useRef()
		const resolvedRef = ref || defaultRef

		React.useEffect(() => {
			resolvedRef.current.indeterminate = indeterminate
		}, [resolvedRef, indeterminate])
		const classes = useStyles();
		return (
			<>
				<Checkbox
					color='default'
					// className={}
					indeterminate={indeterminate}
					ref={resolvedRef} {...rest}
					classes={{
						checked: classes.checked,
						root: classes.checkRoot
					}} />
			</>
		)
	}
);

// Our table component
function Table({
	title,
	columns,
	data,
	fetchData,
	loading,
	rowsPerPage = 10,
	defaultSearchQuery = '',
	defaultPageIndex = 0,
	defaultSortBy = [],
	numberOfRowsData = [5, 10, 15],
	pageCount: controlledPageCount,
	totalRecordsCount,
	detailsPath,
	addRecordComponent,
	defaultDateRangeFilter,
	onDelete,
	onDeleteAll,
	children,
	clickableRows = true,
	rowsWithSelection = true,
	isPageRootComponent = false,
	isFrontEndData = false,
	isAllowDelete = true,
	dateRangeCanBeLaterThanToday = false,
}) {
	const classes = useStyles();

	const [filterQuery, setFilterQuery] = React.useState(defaultSearchQuery);
	const [dateRangeFilter, setDateRangeFilter] = React.useState(defaultDateRangeFilter);
	const [pagingPage, setPagingPage] = React.useState(defaultPageIndex + 1);
	const [searchError, setSearchError] = React.useState(false);
	const maxSearchLength = 255;
	const history = useHistory();

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		prepareRow,
		page,
		rows,
		pageCount,
		setPageSize,
		gotoPage,
		pageOptions,
		selectedFlatRows,
		setGlobalFilter,
		state: { pageIndex, pageSize, sortBy, selectedRowIds }
	} = useTable(
		{
			columns,
			data,
			initialState: {
				pageSize: rowsPerPage,
				globalFilter: filterQuery,
				pageIndex: defaultPageIndex,
				sortBy: defaultSortBy,
			},
			autoResetPage: false,
			manualSortBy: isFrontEndData ? false : true,
			manualPagination: isFrontEndData ? false : true,
			manualGlobalFilter: isFrontEndData ? false : true,
			pageCount: isFrontEndData ? -1 : controlledPageCount
		},
		useGlobalFilter,
		useSortBy,
		usePagination,
		useRowSelect,
		hooks => {
			if (rowsWithSelection) {
				hooks.visibleColumns.push(columns => [
					// Let's make a column for selection
					{
						id: 'selection',
						className: 'selection',
						// The header can use the table's getToggleAllRowsSelectedProps method
						// to render a checkbox
						Header: ({ getToggleAllRowsSelectedProps }) => (
							<IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
						),
						// The cell can use the individual row's getToggleRowSelectedProps method
						// to the render a checkbox
						Cell: ({ row }) => (
							<IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
						),
					},
					...columns,
				]);
			}
		}
	);

	const onFetchDataDebounced = useAsyncDebounce(fetchData, 300);
	const onFetchFrontDataDebounced = useAsyncDebounce(({ filterQuery }) => {
		setGlobalFilter(filterQuery || '');
	}, 300);

	React.useEffect(() => {
		const trimmedQuery = filterQuery.trim();
		if (isFrontEndData) {
			onFetchFrontDataDebounced({ filterQuery: trimmedQuery, dateRangeFilter });
		} else {
			onFetchDataDebounced({ filterQuery: trimmedQuery, dateRangeFilter, pageIndex, pageSize, sortBy });
		}
		isPageRootComponent && scrollToTop();
	}, [onFetchDataDebounced, onFetchFrontDataDebounced, filterQuery, dateRangeFilter, pageIndex, pageSize, sortBy]);

	const handleChange = (event, page) => {
		setPagingPage(page)
		gotoPage(page - 1);
	};

	React.useEffect(() => {
		if (isFrontEndData && pageCount < pagingPage && pagingPage > 1) {
			handleChange(null, pagingPage - 1);
		}
	}, [pageCount, pagingPage]);

	const handleSearch = (value) => {
		const trimmedValue = value.trimStart();
		if (trimmedValue !== filterQuery) {
			handleChange(null, 1);
			setFilterQuery(trimmedValue);
		}
	};

	const handleFilterChange = (partOfPeriod = 'from', value) => {
		setDateRangeFilter((dateRangeFilter) => ({
			...dateRangeFilter,
			[partOfPeriod]: value,
		}));
		handleChange(null, 1);
	};

	const showDetails = (original, target) => {
		if (target.type !== 'checkbox' && clickableRows) {

			history.push({
				pathname: `/${detailsPath}/${original?.id}`,
				state: original?.item,
			});
		}
	};

	const handleDelete = () => {
		onDelete(selectedFlatRows, () => {
			if (selectedFlatRows.length === data.length && pagingPage > 1) {
				handleChange(null, pagingPage - 1);
			} else {
				fetchData({ filterQuery, dateRangeFilter, pageIndex, pageSize, sortBy });
			}
		});
	};

	const getItemsOnPage = () => {
		const currentTotalCount = isFrontEndData ? rows.length : totalRecordsCount;
		const start = currentTotalCount ? pageSize * pageIndex + 1 : 0;
		const preEnd = start + pageSize - 1;
		const end = preEnd > currentTotalCount ? currentTotalCount : preEnd;
		return `${start} - ${end} of ${currentTotalCount}`
	}

	return (
		<>
			<div className='ReactTable -striped -highlight'>
				<GridContainer className={classes.right}>
					<GridItem xs={12} sm={6} md={4}>
						{title &&
							<FormLabel className={`${classes.tableHeader} ${title.withCardIcon ? 'withCardIcon' : ''}`}>
								<div>{title.text}</div>
							</FormLabel>
						}
						<FormLabel className={classes.labelCounter}>
							{selectedFlatRows.length > 0 && selectedFlatRows.length + ' Items selected'}
						</FormLabel>
					</GridItem>
					<GridItem
						className={classes.headerPanel}
						xs={12} sm={6} md={8}
					>
					{dateRangeFilter &&
						<DateRangeFilter
							canBeLaterThanToday={dateRangeCanBeLaterThanToday}
							name={dateRangeFilter.name}
							displayName={dateRangeFilter.displayName}
							dateFrom={dateRangeFilter.from}
							dateTo={dateRangeFilter.to}
							onChange={(...args) => handleFilterChange(...args)}
						/>
						}
						<div>
							<CustomInput
								formControlProps={{
									className: `${classes.top} ${classes.searchInput}`
								}}
								helperTextClassName={classes.searchHelperText}
								helperText={searchError ? `Search Max length is ${maxSearchLength} characters` : ''}
								inputProps={{
									placeholder: 'Search',
									error: searchError,
									inputProps: {
										'aria-label': 'Search',
										className: classes.searchInput
									},
									value: filterQuery,
									startAdornment: <SearchIcon />,
								}}
								onChange={(event) => {
									if (event.target.value.length > maxSearchLength) {
										setSearchError(true);
										return;
									} else {
										setSearchError(false);
									}
									const newVal = event.target.value.slice(0, maxSearchLength);
									handleSearch(newVal);
								}}
								clearable
								onClear={() => handleSearch('')}
							/>
							{
								!!selectedFlatRows.length
								&& isAllowDelete
								&& <Button
									color="transparent"
									simple
									aria-label="Remove"
									justIcon
									onClick={handleDelete}
								>
									<Delete
										className={classes.menuIconRigth}
									/>
								</Button>
							}
							{onDeleteAll &&
								<ButtonMenuList
									classesnames={`${classes.right} ${classes.buttonRight}`}
									menuItems={[
										{
											key: 'tablemenu',
											label: 'Remove All',
											onClick: () => { onDeleteAll() }
										}
									]}
								>
									<MoreVert className={classes.menuIconRigth} />
								</ButtonMenuList>}

								{addRecordComponent && addRecordComponent}
						</div>
					</GridItem>
				</GridContainer>

				<GridContainer>
					<GridItem className={`${classes.tableContainer} ${!isPageRootComponent ? 'nestedTable' : ''}`} xs={12} sm={12} md={12}>
						<table {...getTableProps()} className='rt-table'>
							<thead className='rt-thead -header'>
								{headerGroups.map(headerGroup => (
									<tr {...headerGroup.getHeaderGroupProps()} className='rt-tr'>
										{headerGroup.headers.map((column, key) => (
											<th
												{...column.getHeaderProps(column.getSortByToggleProps())}
												className={classnames('rt-th rt-resizable-header', {
													'-cursor-pointer': (!rowsWithSelection || 0 !== key) && !column.disableSortBy,
													'-sort-asc': column.isSorted && !column.isSortedDesc,
													'-sort-desc': column.isSorted && column.isSortedDesc
												}, column.className || '')}
											>
												<div className='rt-resizable-header-content'>
													{column.render('Header')}
												</div>
												<div className="rt-resizable-header-icon"/>
											</th>
										))}
									</tr>
								))}
							</thead>
							<tbody {...getTableBodyProps()} className='rt-tbody'>
								{!loading && page.map((row, i) => {
									prepareRow(row);
									return (
										<tr
											{...row.getRowProps()}
											key={row.original?.id || row.original?._id || row.original?.tempId}
											className={classnames(
												'rt-tr',
												{ ' -odd': i % 2 === 0 },
												{ ' -even': i % 2 === 1 }
											)}
											onClick={
												(e) => showDetails(row.original, e.target)
											}
										>
											{row.cells.map(cell => {
												return (
													<td {...cell.getCellProps()}
														className={classnames('rt-td', cell.column.className || '')}>
														{cell.render('Cell')}
													</td>
												);
											})}
										</tr>
									);
								})}
							</tbody>
						</table>
						{loading
							? <LoadingIndicator />
							: page.length < 1 &&
							<FormLabel className={classes.labelCentered}>
								No Results Found
							</FormLabel>}
					</GridItem>
				</GridContainer>
				<div className='pagination-bottom'>
					<div className='-pagination'>
						<GridContainer className={classes.gridContainer}>
							<GridItem xs={12} sm={6} md={6}>
								<GridContainer>
									<GridItem xs={12} sm={6} md={10}>
										{children}
									</GridItem>
									<GridItem className={classes.rowsPerPageContainer} xs={12} sm={6} md={2}>
										<FormControl
											fullWidth
											className={`${classes.selectFormControl} ${classes.formControlMargins} ${classes.rowsPerPageControl}`}
										>
											<InputLabel
												htmlFor="simple-select"
												className={classes.selectLabel}
											>
												Rows per page
											</InputLabel>
											<Select
												MenuProps={{
													className: classes.selectMenu
												}}
												classes={{
													select: classes.select
												}}
												value={pageSize}
												onChange={event => {
													handleChange(null, 1);
													setPageSize(event.target.value);
												}}
												inputProps={{
													name: 'numberOfRows',
													id: 'number-of-rows'
												}}
											>
												{numberOfRowsData.map(prop => {
													return (
														<MenuItem
															key={prop}
															classes={{
																root: classes.selectMenuItem,
																selected: classes.selectMenuItemSelected
															}}
															value={prop}
														>
															{prop} rows
														</MenuItem>
													);
												})}
											</Select>
										</FormControl>
									</GridItem>
								</GridContainer>
							</GridItem>
							<GridItem xs={12} sm={6} md={6}>
								<Pagination
									count={pageCount}
									page={pagingPage}
									siblingCount={0}
									onChange={handleChange}
								/>
								<FormLabel className={classes.labelHorizontal}>
									{getItemsOnPage()}
								</FormLabel>
							</GridItem>
						</GridContainer>
					</div>
				</div>
			</div>
		</>
	);
}

// Define a custom filter filter function!
// function filterGreaterThan(rows, id, filterValue) {
// 	return rows.filter(row => {
// 		const rowValue = row.values[id];
// 		return rowValue >= filterValue;
// 	});
// }

// This is an autoRemove method on the filter function that
// when given the new filter value and returns true, the filter
// will be automatically removed. Normally this is just an undefined
// check, but here, we want to remove the filter if it's not a number
// filterGreaterThan.autoRemove = val => typeof val !== 'number';

export default Table;
